import type { SuggestedPrompt } from "@lobu/core";
import { createContext, useContext } from "react";
import {
	entityTypeSegment,
	OWNER_SECTIONS,
	type OwnerSectionKey,
} from "@/lib/owner-sections";

/** Where the ask command was invoked. Pure routing context, no IDs. */
export interface AssistSeed {
	owner: string;
	section: OwnerSectionKey | "settings";
	/** Data views: the entity type's display name (e.g. "Invoice"). */
	entityTypeName?: string;
	/** Filtered-empty states: a short human summary of the active filter. */
	filterSummary?: string;
}

interface AssistContent {
	/** Display noun driving labels ("Automations", "Invoices"). */
	noun: string;
	/**
	 * Starter chips for the empty composer. Each message names the workspace
	 * and page, so the agent knows where the user asked from.
	 */
	starters: SuggestedPrompt[];
}

function nounFor(seed: AssistSeed): string {
	if (seed.section === "settings") return "Settings";
	if (seed.section === "context") return seed.entityTypeName ?? "Memory";
	return OWNER_SECTIONS[seed.section].label;
}

function startersFor(seed: AssistSeed, noun: string): SuggestedPrompt[] {
	const scope = `Context: ${seed.owner} ${seed.section === "settings" ? "settings" : seed.section === "context" && seed.entityTypeName ? seed.entityTypeName : OWNER_SECTIONS[seed.section].segment}`;
	const ask = (title: string, request: string) => ({
		title,
		message: `${scope} — ${request}`,
	});
	const items = noun.toLowerCase();
	if (seed.filterSummary) {
		return [
			ask(
				"Draft one",
				`no ${items} match (${seed.filterSummary}). Draft one that fits.`,
			),
			ask(
				"Why no match?",
				`no ${items} match (${seed.filterSummary}). Explain why the filter matches nothing.`,
			),
		];
	}
	switch (seed.section) {
		case "settings":
			return [
				ask(
					"Explain my settings",
					"explain this workspace's settings, policies, and governance.",
				),
			];
		case "automations":
			return [
				ask(
					"Draft automation",
					"draft an automation for this workspace so I can approve each step.",
				),
				ask(
					"What failed?",
					"which automations ran recently, and did any fail?",
				),
				ask(
					"Ideas for me",
					"suggest an automation that would save me time with the data I have connected.",
				),
			];
		case "agents":
			return [
				ask(
					"Draft an agent",
					"draft a managed agent for this workspace (role, tools, guardrails) for my approval.",
				),
				ask(
					"Review my agents",
					"review my agents and point out gaps in their tools or guardrails.",
				),
			];
		case "connectors":
			return [
				ask(
					"What to add next",
					"recommend which connector to add next and draft its setup steps.",
				),
				ask(
					"Check connections",
					"check my connections and tell me which are failing or stale.",
				),
			];
		case "activity":
			return [
				ask("Needs attention", "summarize what needs attention here."),
				ask("What changed today", "what changed in this workspace today?"),
			];
		case "context":
			return seed.entityTypeName
				? [
						ask("Add a row", `draft a new ${items} row.`),
						ask("Summarize", `summarize what is in ${noun} right now.`),
						ask(
							"What feeds this?",
							`explain what feeds ${noun} and how it stays up to date.`,
						),
					]
				: [
						ask(
							"What lives here?",
							"explain what lives in this view and how to add to it.",
						),
					];
	}
}

/** "service-invoices" -> "Service invoices": a display name from a route segment. */
function humanizeSegment(segment: string): string {
	let text = segment;
	try {
		text = decodeURIComponent(segment);
	} catch {
		// keep the raw segment
	}
	text = text.replace(/[-_]+/g, " ").trim();
	return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Memory";
}

export function seedForRoute(owner: string, pathname: string): AssistSeed {
	// Segment-exact: `/acme/agents-metadata` is an entity type, not Agents.
	const first = pathname.startsWith(`/${owner}/`)
		? pathname.slice(owner.length + 2).split("/")[0]
		: "";
	switch (first) {
		case "settings":
			return { owner, section: "settings" };
		case "automations":
			return { owner, section: "automations" };
		case "agents":
			return { owner, section: "agents" };
		case "connectors":
			return { owner, section: "connectors" };
		case "events":
			return { owner, section: "activity" };
		case "data":
			return { owner, section: "context" };
	}
	// First-class entity-type paths (`/{owner}/<type>[/…]`) resolve to Data;
	// the shared helper excludes owner routes, settings and reserved segments.
	const entityType = entityTypeSegment(owner, pathname);
	if (entityType)
		return {
			owner,
			section: "context",
			entityTypeName: humanizeSegment(entityType),
		};
	return { owner, section: "activity" };
}

/**
 * Derives every assist string from routing context. Pure: same seed in,
 * same content out.
 */
export function resolveAssistContent(seed: AssistSeed): AssistContent {
	const noun = nounFor(seed);
	return {
		noun,
		starters: startersFor(seed, noun),
	};
}

export interface AssistApi {
	open: (seed: AssistSeed) => void;
	close: () => void;
	isOpen: boolean;
}

export const AssistContext = createContext<AssistApi | null>(null);

export function useAssistInternal(): AssistApi {
	const api = useContext(AssistContext);
	if (!api) throw new Error("AssistProvider is missing");
	return api;
}
